import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Nullable } from '../../types';

export function useResizeObserver<ElemType extends Element = HTMLDivElement>(
  attributeKey: 'width',
): {
  elementRef: MutableRefObject<Nullable<ElemType>>;
  onSetRef: (newElement: Nullable<ElemType>) => void;
  width: number;
};

export function useResizeObserver<ElemType extends Element = HTMLDivElement>(
  attributeKey: 'height',
): {
  elementRef: MutableRefObject<Nullable<ElemType>>;
  onSetRef: (newElement: Nullable<ElemType>) => void;
  height: number;
};

export function useResizeObserver<ElemType extends Element = HTMLDivElement>(
  attributeKey: 'both',
): {
  elementRef: MutableRefObject<Nullable<ElemType>>;
  onSetRef: (newElement: Nullable<ElemType>) => void;
  width: number;
  height: number;
};

export function useResizeObserver<ElemType extends Element = HTMLDivElement>(
  attributeKey: 'width' | 'height' | 'both',
) {
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);
  const [readyToObserve, setReadyToObserve] = useState<boolean>(false);
  const onElementResize = useCallback(
    (contentRect: DOMRectReadOnly) => {
      setWidth(contentRect.width);
      setHeight(contentRect.height);
    },
    [setWidth, setHeight],
  );
  const onResizeObserve = useCallback(
    (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        onElementResize(entry.contentRect);
      }
    },
    [onElementResize],
  );
  const elementRef = useRef<Nullable<ElemType>>(null);
  const observerRef = useRef<Nullable<ResizeObserver>>(null);

  useEffect(() => {
    const observer = new ResizeObserver(onResizeObserve);
    observerRef.current = observer;
    setReadyToObserve(true);

    return () => {
      observer.disconnect();
    };
  }, [onResizeObserve]);

  const onSetRef = useCallback(
    (newElement: Nullable<ElemType>) => {
      if (!observerRef.current || !readyToObserve) return;
      if (elementRef.current === newElement) return;
      if (elementRef.current) {
        observerRef.current.unobserve(elementRef.current);
      }
      if (newElement) {
        observerRef.current.observe(newElement);
      }
      elementRef.current = newElement;
    },
    [readyToObserve],
  );

  if (attributeKey === 'both') {
    return {
      elementRef,
      onSetRef,
      width,
      height,
    };
  }
  if (attributeKey === 'width') {
    return {
      elementRef,
      onSetRef,
      width,
    };
  }
  return {
    elementRef,
    onSetRef,
    height,
  };
}
